import type { ClientMessage, ServerMessage } from '../shared/types.js';

/** Reconnecting WebSocket to the control server, typed both ways. */
export class Bus {
  private ws: WebSocket | null = null;
  /**
   * The last registration, REPLAYED on every open rather than queued once.
   *
   * This was a queue, and a queue drains. The first `open` sent the register
   * and emptied it, so every reconnect after that carried no registration at
   * all, while the line above it claimed registration survived a reconnect.
   * The server holds a feed id in the socket's own closure and drops vitals
   * from a connection that never registered (`server/index.ts`), so a publisher
   * that reconnected went silent and STAYED silent, with the page still saying
   * "Connected to the switchboard". Nothing failed; it just stopped counting.
   *
   * Replaying is safe by construction rather than by care: `Switchboard.register`
   * returns the existing feed for a path it already knows, so a replay lands on
   * the same feed id instead of opening a second one.
   */
  private registration: ClientMessage | null = null;
  private closed = false;

  constructor(
    private readonly url: string,
    private readonly onMessage: (msg: ServerMessage) => void,
    private readonly onStatus?: (up: boolean) => void,
  ) {
    this.connect();
  }

  private connect(): void {
    if (this.closed) return;
    const ws = new WebSocket(this.url);
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.onStatus?.(true);
      // Every open, not just the first. This is the whole fix.
      if (this.registration !== null) this.send(this.registration);
    });

    ws.addEventListener('message', (ev) => {
      try {
        this.onMessage(JSON.parse(String(ev.data)) as ServerMessage);
      } catch {
        // A malformed frame is not worth killing the broadcast over.
      }
    });

    ws.addEventListener('close', () => {
      this.onStatus?.(false);
      if (!this.closed) setTimeout(() => this.connect(), 1000);
    });

    ws.addEventListener('error', () => ws.close());
  }

  send(msg: ClientMessage): void {
    // Retained BEFORE the readyState check, so the registration sent while the
    // very first socket is still CONNECTING is replayed on open rather than
    // dropped. That race is what the old queue existed for, and it is kept.
    if (msg.type === 'register') this.registration = msg;
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
    // Everything else is disposable on purpose: vitals, ratings and takes all
    // describe a moment that has passed by the time a reconnect completes.
  }

  close(): void {
    this.closed = true;
    this.ws?.close();
  }
}

export function wsUrl(path = '/ws'): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  /*
   * §5's viewer and director: `?channel=<id>` scopes the state frames to one
   * board. Carried from THIS page's own query string, so `/?channel=live` and
   * `/director.html?channel=live` both watch that channel and a page opened
   * without one keeps getting the node's, which is every surface built before
   * this stage.
   *
   * A query parameter rather than `/watch/<channel>`, and the reason is stated
   * rather than left as a shortfall: the node serves ONE page for watching and
   * publishing, since wave 5 merged them, so a path segment would need a router
   * in front of a static file to arrive at the same document. The site's
   * `/channels/<id>` is a real path because it is a real page.
   */
  const channel = new URLSearchParams(location.search).get('channel');
  const scoped = channel === null || channel === ''
    ? path
    : `${path}?channel=${encodeURIComponent(channel)}`;
  return `${proto}//${location.host}${scoped}`;
}

/**
 * The same socket URL, carrying whatever key this page was opened with.
 *
 * The director's page is reached as `/director.html?key=...` and hands that
 * key straight to its own socket, so the operator pastes one link and nothing
 * else asks them for anything. Wave 5, D9.
 *
 * Only `director.ts` calls this. The viewer and publisher connect with
 * `wsUrl()` and are refused every privileged message by construction, which is
 * the correct outcome and not a limitation: neither page has one to send.
 */
export function privilegedWsUrl(path = '/ws'): string {
  const key = new URLSearchParams(location.search).get('key');
  if (key === null) return wsUrl(path);
  /*
   * `wsUrl` may already carry `?channel=`, so the key is appended with the
   * separator the URL actually needs rather than an assumed `?`. Getting that
   * wrong produces `?channel=live?key=...`, which parses as one parameter whose
   * value contains the key: a privileged socket silently demoted to a viewer.
   */
  const base = wsUrl(path);
  return `${base}${base.includes('?') ? '&' : '?'}key=${encodeURIComponent(key)}`;
}
